import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, tokenStore } from "@/lib/api";


const SettingsContext = createContext(null);

const FALLBACK = {
  hero_title: "Création et entretien d'espaces verts",
  hero_subtitle: "Des jardins soignés, durables et adaptés à vos envies.",
  hero_image: "",
  cta_label: "Demander un devis",
  about_title: "Le savoir-faire d'un paysagiste passionné",
  about_text: "",
  about_image: "",
  about_values: [],
  targets: {
    particuliers_title: "Particuliers",
    particuliers_text: "",
    professionnels_title: "Professionnels & régies",
    professionnels_text: "",
  },
  contact_phone: "",
  contact_whatsapp: "",
  contact_email: "",
  contact_address: "",
  contact_hours: "",
  instagram_url: "",
  facebook_url: "",
  partners: [],
  seo_title: "",
  seo_description: "",
  seo_keywords: "",
  company_name: "Pizzelli Paysagiste",
  company_tagline: "",
  logo: "",
  logo_light: "",
  logo_size: 40,
  logo_light_size: 48,
  partners_logo_size: 80,
};

const SETTINGS_CACHE_KEY = "pizzelli_settings_cache_v3";

function readCachedSettings() {
  // Purge des anciennes versions (v1, v2)
  try {
    if (typeof window !== "undefined") {
      const keys = Object.keys(window.localStorage);
      keys.forEach((k) => {
        if (/^pizzelli_settings_cache_v\d+$/.test(k) && k !== SETTINGS_CACHE_KEY) {
          window.localStorage.removeItem(k);
        }
      });
    }
  } catch {}
  try {
    const raw = localStorage.getItem(SETTINGS_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeCachedSettings(s) {
  try {
    localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(s));
  } catch {
    // quota exceeded (base64 logo trop gros) -> on ignore silencieusement
  }
}

export function SettingsProvider({ children }) {
  const cached = readCachedSettings();
  const [settings, setSettings] = useState(cached ? { ...FALLBACK, ...cached } : FALLBACK);
  const [loaded, setLoaded] = useState(!!cached);
  const [fresh, setFresh] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/site/settings");
      const merged = { ...FALLBACK, ...data };
      setSettings(merged);
      writeCachedSettings(data);
      setFresh(true);
    } catch (e) {
      // keep cached/fallback
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (settings?.seo_title) document.title = settings.seo_title;
    const desc = document.querySelector('meta[name="description"]');
    if (desc && settings?.seo_description) desc.setAttribute("content", settings.seo_description);
  }, [settings]);

  return (
    <SettingsContext.Provider value={{ settings, setSettings, refresh, loaded, fresh }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);

// ---------------- Auth ----------------
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking, null = anonymous, object = authed

  const fetchMe = useCallback(async () => {
    if (!tokenStore.get()) {
      setUser(null);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      tokenStore.clear();
      setUser(null);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) tokenStore.set(data.access_token);
    setUser({ id: data.id, email: data.email, role: data.role });
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    tokenStore.clear();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
